/**
 * api.js — Centralized API endpoint definitions.
 *
 * All functions return an Axios response promise: { data, status, headers, ... }.
 * The actual payload is in response.data. Callers must do `const res = await api.xyz(); res.data`.
 *
 * AUTH: Every request goes through apiClient.js interceptors which attach the Bearer token
 * and handle 401 refresh/retry automatically. No auth logic needed here.
 *
 * CACHE BEHAVIOR:
 *   - getCached(key, fetcher): in-memory cache with 60s TTL. Returns stale data within TTL
 *     without hitting the server. Cache is per-page-session (cleared on full page reload).
 *   - clearCache(pattern): deletes all cache entries whose key includes the pattern string.
 *   - Cached endpoints: getParts (key: "parts-{projectId}-{subsystemId}"),
 *     getSystemHierarchy (key: "hierarchy").
 *   - Mutating endpoints (createPart, updatePart, deletePart, createSubsystem,
 *     updateSubsystem, deleteSubsystem) call clearCache() after success.
 *   - Side-effect: if you add a new read endpoint that depends on parts or hierarchy data,
 *     you must either cache-bust it in the relevant mutators or skip caching.
 *   - Side-effect: clearCache('parts') wipes ALL parts cache entries across all
 *     project/subsystem combinations, not just the one that changed.
 *
 * RESPONSE FORMATS (all wrapped in Axios response — access via .data):
 *   - List endpoints: { items: [...] } or bare [...] depending on the backend route.
 *   - Single-entity endpoints: the entity object directly.
 *   - Mutating endpoints: the created/updated entity or { message: "..." }.
 *   - File/download endpoints: { url: "..." } (presigned S3 URL).
 *   - Streaming endpoints (invokeOrchestrator): response is a ReadableStream.
 *   Check the backend route for the exact shape; this file does not transform responses.
 */
import client from './apiClient';

// All endpoints are prefixed with this. Relative to apiClient's baseURL.
// Side-effect: changing this breaks every endpoint if the backend prefix differs.
const API_BASE_URL = '/api/v1'; // Relative to client baseURL

// ─── In-Memory Cache ────────────────────────────────────────────────────────
// Simple TTL cache. Not shared across tabs. Cleared on page reload.
// Only used by getParts and getSystemHierarchy to reduce redundant fetches
// when navigating between views that display the same data.
const cache = {};
const CACHE_TTL = 60000; // 1 minute

/**
 * Return cached data if fresh; otherwise execute fetcher and cache the result.
 * NOTE: The entire Axios response object is cached (not just .data).
 * If two components call the same cached endpoint simultaneously before the first
 * resolves, both will make network requests (no in-flight dedup).
 */
const getCached = async (key, fetcher) => {
    const now = Date.now();
    if (cache[key] && (now - cache[key].timestamp < CACHE_TTL)) {
        return cache[key].data;
    }
    const response = await fetcher();
    cache[key] = { timestamp: now, data: response };
    return response;
};

/**
 * Delete all cache entries whose key contains the given pattern string.
 * Example: clearCache('parts') removes "parts-proj1-sub1", "parts-proj2-sub2", etc.
 * Example: clearCache('hierarchy') removes the single "hierarchy" entry.
 */
const clearCache = (pattern) => {
    Object.keys(cache).forEach(key => {
        if (key.includes(pattern)) {
            delete cache[key];
        }
    });
};

export const api = {

    // ═══════════════════════════════════════════════════════════════════════════
    // USERS & GROUPS
    // Response: getUsers → { users: [...] }, createUser/createGroup → created entity
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (loads user list on mount)
    getUsers: () => client.get(`${API_BASE_URL}/auth/users`),
    // Called by: (admin flows — not directly referenced in current components)
    createUser: (userData) => client.post(`${API_BASE_URL}/users`, userData),
    // Called by: (admin flows — not directly referenced in current components)
    createGroup: (groupData) => client.post(`${API_BASE_URL}/groups`, groupData),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECTS
    // Response: getProjects → [...projects], getProject → single project object
    // Side-effect: deleteProject cascades on the backend (branches, files, etc.)
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (project selector), ProjectDashboard, ProductPortfolioView
    getProjects: () => client.get(`${API_BASE_URL}/projects`),
    // Called by: ProjectView (loads project details)
    getProject: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}`),
    // Called by: ProjectDashboard (new project dialog)
    createProject: (projectData) => client.post(`${API_BASE_URL}/projects`, projectData),
    // Called by: ProjectView (inline edit of project name/description)
    updateProject: (projectId, projectData) => client.put(`${API_BASE_URL}/projects/${projectId}`, projectData),
    // Called by: ProjectDashboard (delete project action)
    deleteProject: (projectId, userId) => client.delete(`${API_BASE_URL}/projects/${projectId}`, { params: { user_id: userId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // BRANCHES
    // Response: getBranches → [...branches], each branch has { branch_id, status, ... }
    // Side-effect: deleteBranch is irreversible; all uncommitted changes on it are lost.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: App.js (branch selector), BranchesView, DocumentsView, CADModelsView, UnifiedCADView
    getBranches: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/branches`),
    // Called by: BranchesView (create branch dialog)
    createBranch: (projectId, branchData) => client.post(`${API_BASE_URL}/projects/${projectId}/branches`, branchData),
    // Called by: DocumentsView, CADModelsView, CategoryFileView, UnifiedCADView (file listing by branch)
    getBranchFiles: (projectId, branchId, fileType) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files`,
        { params: { file_type: fileType } }
    ),
    // Called by: BranchesView (delete branch action)
    deleteBranch: (projectId, branchId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}`,
        { params: { user_id: userId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // VERSION CONTROL — Staging & Commits
    // These endpoints manage the git-like staging area for file changes.
    // Note: projectId is typically extracted from branchId format "PROJECT_ID-branch_name".
    // Response: getPendingChanges → { staged: [...], unstaged: [...] }
    //           commitChanges → { commit_id, message, ... }
    // Side-effect: commitChanges finalizes staged changes; they become immutable.
    // Side-effect: discardAllChanges permanently drops all unstaged/staged changes.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: PendingChangesPanel (loads pending changes for the current branch)
    getPendingChanges: (projectId, branchId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/pending`
    ),
    // Called by: (staging is typically triggered from file upload/edit flows)
    stageFile: (projectId, branchId, fileId, versionId, fileName) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage`,
        { file_id: fileId, version_id: versionId, file_name: fileName }
    ),
    // Called by: (staging a file deletion before commit)
    stageFileDeletion: (projectId, branchId, fileId, fileName) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage-delete`,
        { file_id: fileId, file_name: fileName }
    ),
    // Called by: PendingChangesPanel (unstage action per file)
    unstageFile: (projectId, branchId, fileId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/stage/${fileId}`
    ),
    // Called by: (re-staging a specific version of a file)
    restageVersion: (projectId, branchId, versionId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/restage/${versionId}`
    ),
    // Called by: PendingChangesPanel (commit button)
    commitChanges: (projectId, branchId, message, authorName, authorEmail, milestoneId, milestoneProgress) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/commits`,
        { message, author_name: authorName, author_email: authorEmail, milestone_id: milestoneId || null, milestone_progress: milestoneProgress ?? null }
    ),
    // Called by: PendingChangesPanel (discard all changes button)
    discardAllChanges: (projectId, branchId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/pending`
    ),
    // Called by: BranchDiffDialog (compare branches)
    getBranchDiff: (projectId, branchId, compareTo) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/diff`,
        { params: { compare_to: compareTo } }
    ),
    // Called by: CommitHistory (commit log), TagManager (commit picker for tags)
    getCommitHistory: (projectId, branchId, limit = 50) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/commits`,
        { params: { limit } }
    ),
    // Called by: CommitHistory (expand commit to see file diffs)
    getCommitDiff: (projectId, commitId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/commits/${commitId}/diff`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // MERGE OPERATIONS
    // Response: previewMerge → { conflicts: [...], changes: [...] }
    //           createMergeRequest → { merge_request_id, ... }
    //           executeMerge → { commit_id, ... }
    // Side-effect: executeMerge creates a merge commit on the target branch.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: MergeDialog (preview step)
    previewMerge: (projectId, sourceBranchId, targetBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${sourceBranchId}/merge-preview`,
        null,
        { params: { target_branch_id: targetBranchId } }
    ),
    // Called by: MergeDialog (create MR step)
    createMergeRequest: (projectId, sourceBranchId, targetBranchId, title, description, ecoId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${sourceBranchId}/merge-requests`,
        { target_branch_id: targetBranchId, title, description, eco_id: ecoId }
    ),
    // Called by: MergeDialog (fetch conflicts after MR creation)
    getMergeConflicts: (projectId, branchId, mergeRequestId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/merge-requests/${mergeRequestId}/conflicts`
    ),
    // Called by: MergeDialog (execute merge with optional conflict resolutions)
    executeMerge: (projectId, branchId, mergeRequestId, conflictResolutions) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/merge-requests/${mergeRequestId}/execute`,
        { conflict_resolutions: conflictResolutions }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // REBASE OPERATIONS
    // Response: previewRebase → { changes: [...], conflicts: [...] }
    //           executeRebase → { commit_id, ... }
    // Side-effect: executeRebase rewrites the branch's commit history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (rebase UI — not directly referenced in current components)
    previewRebase: (projectId, branchId, ontoBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/rebase-preview`,
        { onto_branch_id: ontoBranchId }
    ),
    executeRebase: (projectId, branchId, ontoBranchId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/rebase`,
        { onto_branch_id: ontoBranchId }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // CHERRY-PICK & REVERT
    // Side-effect: cherryPick creates a new commit on the target branch.
    // Side-effect: revertCommit creates a new "undo" commit — does not delete history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CommitHistory (cherry-pick action on a commit row)
    cherryPick: (projectId, branchId, commitId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/cherry-pick`,
        { commit_id: commitId }
    ),
    // Called by: CommitHistory (revert action on a commit row)
    revertCommit: (projectId, branchId, commitId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/revert`,
        null,
        { params: { commit_id: commitId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // TAGS
    // Response: getTags → [...tags], each tag has { tag_id, tag_name, tag_type, commit_id }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: TagManager (create tag dialog)
    createTag: (projectId, branchId, commitId, tagName, tagType, message) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags`,
        { commit_id: commitId, tag_name: tagName, tag_type: tagType, message }
    ),
    // Called by: TagManager (tag listing)
    getTags: (projectId, branchId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags`
    ),
    // Called by: TagManager (delete tag action)
    deleteTag: (projectId, branchId, tagId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/tags/${tagId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // RELEASES
    // Response: getReleases → [...releases], createRelease → release object
    // Side-effect: publishRelease makes a release immutable and visible to all users.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ReleaseManager (create release form)
    createRelease: (projectId, tagId, version, title, description, releaseNotes) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/releases`,
        { tag_id: tagId, version, title, description, release_notes: releaseNotes }
    ),
    // Called by: ReleaseManager (release listing, includeDrafts=true)
    getReleases: (projectId, includeDrafts = false) => client.get(
        `${API_BASE_URL}/projects/${projectId}/branches/releases`,
        { params: { include_drafts: includeDrafts } }
    ),
    // Called by: ReleaseManager (publish action)
    publishRelease: (projectId, releaseId) => client.post(
        `${API_BASE_URL}/projects/${projectId}/branches/releases/${releaseId}/publish`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // EXPERIMENTS
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components)
    createExperiment: (experimentData) => client.post(`${API_BASE_URL}/experiments`, experimentData),

    // ═══════════════════════════════════════════════════════════════════════════
    // PERMISSIONS
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components)
    requestPermission: (requestData) => client.post(`${API_BASE_URL}/permissions/request`, requestData),

    // ═══════════════════════════════════════════════════════════════════════════
    // ACL (Access Control List)
    // Response: getMyPermission → { permission_level: "owner"|"editor"|"viewer"|... }
    //           getProjectPermissions → [...acl entries]
    // Side-effect: grantPermission/updatePermission/revokePermission changes who can
    //   access the project. This affects all project-scoped operations.
    // ═══════════════════════════════════════════════════════════════════════════
    // Project ACL endpoints. Currently no UI calls them (RBAC is flat as of
    // 2026-04-27); kept so a future RBAC iteration can wire them back in.
    getMyPermission: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/acl/my-permission`
    ),
    getProjectPermissions: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/acl`
    ),
    grantPermission: (projectId, userId, permissionLevel, expiresAt = null) => client.post(
        `${API_BASE_URL}/projects/${projectId}/acl`,
        { user_id: userId, permission_level: permissionLevel, expires_at: expiresAt }
    ),
    updatePermission: (projectId, userId, permissionLevel, expiresAt = null) => client.put(
        `${API_BASE_URL}/projects/${projectId}/acl/${userId}`,
        { user_id: userId, permission_level: permissionLevel, expires_at: expiresAt }
    ),
    revokePermission: (projectId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/acl/${userId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // FILE MANAGEMENT (S3-based files — CAD models, analysis results)
    // These endpoints deal with raw S3 files (upload/download/delete).
    // Response: uploadFile → { document_id, s3_key, ... }, getS3Files → [...files]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: FileUpload, CADModelsView, UnifiedCADView (file upload)
    // NOTE: Content-Type is set to undefined to let the browser set multipart boundary.
    uploadFile: (formData, onProgress) => {
        const file = formData.get('file');
        const sizeMB = file ? file.size / (1024 * 1024) : 0;
        const timeout = Math.max(120000, sizeMB * 10000); // min 2min, +10s per MB
        return client.post(`${API_BASE_URL}/files/upload`, formData, {
            headers: { 'Content-Type': undefined },
            onUploadProgress: onProgress,
            timeout,
        });
    },
    // Called by: (S3-based file listing — used alongside getDocuments in some views)
    getS3Files: (projectId, fileType) => client.get(`${API_BASE_URL}/files`, { params: { project_id: projectId, file_type: fileType } }),
    // S3-specific file deletion (distinct from document-based deleteFile below)
    deleteS3File: (fileId) => client.delete(`${API_BASE_URL}/files/${fileId}`),
    updateS3FileDescription: (filename, description) => client.put(`${API_BASE_URL}/files/${encodeURIComponent(filename)}/description`, { description }),
    // Called by: VersionHistoryPanel (load version list for a file)
    getFileVersionHistory: (fileId) => client.get(`${API_BASE_URL}/files/${fileId}/versions`),
    // Called by: (version comparison UI)
    compareVersions: (version1Id, version2Id, comparisonType = 'combined') => client.post(`${API_BASE_URL}/versions/compare`, {
        version1_id: version1Id,
        version2_id: version2Id,
        comparison_type: comparisonType
    }),

    // ═══════════════════════════════════════════════════════════════════════════
    // FILE MAINTENANCE (cleanup orphaned S3 objects / DB records)
    // Response: findOrphanedFiles → { orphaned_s3: [...], orphaned_db: [...] }
    // Side-effect: cleanupOrphanedFiles permanently deletes files. Irreversible.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (admin/maintenance UI — not directly referenced in current components)
    findOrphanedFiles: () => client.get(`${API_BASE_URL}/files/maintenance/orphans`),
    cleanupOrphanedFiles: (cleanupS3 = true, cleanupDb = true) =>
        client.post(`${API_BASE_URL}/files/maintenance/cleanup`, null, { params: { cleanup_s3: cleanupS3, cleanup_db: cleanupDb } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT-BASED FILES (from database — for chat, documents view)
    // These use the /documents endpoint (DB-backed metadata + S3 storage).
    // Response: getFiles → [...documents], getDocument → single document object
    //           downloadFile/downloadVersion → { url: "presigned-s3-url" }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ChatContainer (fetches files for context), DocumentsView
    getFiles: (projectId, fileType) => client.get(`${API_BASE_URL}/documents`, { params: { project_id: projectId, type: fileType } }),
    // Called by: FileDetailDrawer, CADFileDetailDrawer (load document metadata)
    getDocument: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: DocumentsView, CADModelsView, UnifiedCADView,
    //           FileDetailDrawer (delete document action)
    deleteFile: (documentId) => client.delete(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: (update description inline)
    updateFileDescription: (documentId, description) => client.put(`${API_BASE_URL}/documents/${documentId}`, { description }),
    // Called by: (download action — returns presigned URL)
    downloadFile: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}/download`),
    // Called by: VersionHistoryPanel (download a specific version)
    downloadVersion: (versionId) => client.get(`${API_BASE_URL}/versions/${versionId}/download`),
    // Called by: (remove a file from a branch's file list)
    removeBranchFile: (projectId, branchId, fileId, userId) => client.delete(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files/${fileId}`,
        { params: { user_id: userId } }
    ),
    // Called by: CategoryFileView (update file category/metadata)
    updateFileMetadata: (projectId, branchId, fileId, metadata, userId) => client.patch(
        `${API_BASE_URL}/projects/${projectId}/branches/${branchId}/files/${fileId}/metadata`,
        metadata,
        { params: { user_id: userId } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // PARTS & BOMs
    // CACHE: getParts is cached with key "parts-{projectId}-{subsystemId}" (60s TTL).
    //   createPart, updatePart, deletePart all clear both "parts" and "hierarchy" caches.
    // Response: getParts → [...parts], getPart → single part with BOM children
    //           diffBOM → { added: [...], removed: [...], changed: [...] }
    // Side-effect: deletePart may cascade to BOM relationships and linked documents.
    // Side-effect: createPart/updatePart/deletePart invalidate BOTH "parts" and "hierarchy"
    //   caches, which affects getParts and getSystemHierarchy return values.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: PartsView, AssemblyView, FileUpload, SubsystemDetailView,
    //           AssemblyDetailView (load parts list)
    getParts: (projectId, subsystemId) => getCached(
        `parts-${projectId}-${subsystemId}`,
        () => client.get(`${API_BASE_URL}/parts`, { params: { project_id: projectId, subsystem_id: subsystemId } })
    ),
    // Called by: AssemblyDetailView (load child part details)
    getPart: (partId) => client.get(`${API_BASE_URL}/parts/${partId}`),
    // Called by: PartsView (new part form), NewAssemblyDialog
    // Cache: clears "parts" + "hierarchy" on success
    createPart: async (partData, parentAssemblyId) => {
        const res = await client.post(`${API_BASE_URL}/parts`, partData, { params: { parent_assembly_id: parentAssemblyId } });
        clearCache('parts'); // Invalidate parts cache
        clearCache('hierarchy');
        return res;
    },
    // Called by: PartsView (edit part form), NewAssemblyDialog (edit mode)
    // Cache: clears "parts" + "hierarchy" on success
    updatePart: async (partId, partData) => {
        const res = await client.put(`${API_BASE_URL}/parts/${partId}`, partData);
        clearCache('parts');
        clearCache('hierarchy');
        return res;
    },
    // Called by: PartsView (delete part action), AssemblyView (delete assembly)
    // Cache: clears "parts" + "hierarchy" on success
    deletePart: async (partId) => {
        const res = await client.delete(`${API_BASE_URL}/parts/${partId}`);
        clearCache('parts');
        clearCache('hierarchy');
        return res;
    },
    // Called by: (BOM diff comparison UI)
    diffBOM: (partId, compareToPartId) => client.post(`${API_BASE_URL}/parts/${partId}/bom/diff`, null, { params: { compare_to_part_id: compareToPartId } }),
    // Called by: (where-used analysis for a part)
    getPartWhereUsed: (partId) => client.get(`${API_BASE_URL}/parts/${partId}/where-used`),

    // ═══════════════════════════════════════════════════════════════════════════
    // PRODUCT STRUCTURE (PBS — Product Breakdown Structure)
    // CACHE: getSystemHierarchy is cached with key "hierarchy" (60s TTL).
    //   createSubsystem, updateSubsystem, deleteSubsystem clear "hierarchy" cache.
    //   createPart, updatePart, deletePart also clear "hierarchy" cache.
    // Response: getProductTree → nested tree { id, children: [...] }
    //           getSystemHierarchy → { systems: [{ subsystems: [{ parts: [...] }] }] }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ProductStructureView (tree rendering)
    getProductTree: (rootPartId) => client.get(`${API_BASE_URL}/product-structure/tree/${rootPartId}`),
    // Called by: ProductStructureView (node click → detail panel)
    getNodeDetails: (partId) => client.get(`${API_BASE_URL}/product-structure/node/${partId}`),
    // Called by: PartsView, NewAssemblyDialog, NewSubsystemDialog (hierarchy picker/display)
    // Cache: key "hierarchy", 60s TTL. Invalidated by part and subsystem mutations.
    getSystemHierarchy: () => getCached(
        'hierarchy',
        () => client.get(`${API_BASE_URL}/product-structure/hierarchy`)
    ),
    // Called by: SystemView (system listing)
    getSystems: (projectId) => client.get(`${API_BASE_URL}/product-structure/systems`, { params: { project_id: projectId } }),
    // Called by: SubsystemView (subsystem listing under a system)
    getSubsystems: (systemId, projectId) => client.get(`${API_BASE_URL}/product-structure/subsystems`, { params: { system_id: systemId, project_id: projectId } }),
    // Called by: NewSubsystemDialog (edit mode)
    // Cache: clears "hierarchy" on success
    createSubsystem: async (subsystemData) => {
        const res = await client.post(`${API_BASE_URL}/product-structure/subsystems`, subsystemData);
        clearCache('hierarchy');
        return res;
    },
    // Called by: NewSubsystemDialog (edit mode)
    // Cache: clears "hierarchy" on success
    updateSubsystem: async (subsystemId, subsystemData) => {
        const res = await client.put(`${API_BASE_URL}/product-structure/subsystems/${subsystemId}`, subsystemData);
        clearCache('hierarchy');
        return res;
    },
    // Called by: SubsystemView (delete action), PartsView (delete subsystem action)
    // Cache: clears "hierarchy" on success
    deleteSubsystem: async (subsystemId) => {
        const res = await client.delete(`${API_BASE_URL}/product-structure/subsystems/${subsystemId}`);
        clearCache('hierarchy');
        return res;
    },

    // ═══════════════════════════════════════════════════════════════════════════
    // INTERFACES (connections between subsystems)
    // Response: getInterfaces → [...interfaces]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: SubsystemDetailView (interface listing)
    getInterfaces: (subsystemId) => client.get(`${API_BASE_URL}/product-structure/interfaces`, { params: { subsystem_id: subsystemId } }),
    // Called by: NewInterfaceDialog, SubsystemDetailView (quick-add interface)
    createInterface: (interfaceData) => client.post(`${API_BASE_URL}/product-structure/interfaces`, interfaceData),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENTS (engineering documents linked to parts/branches)
    // Response: getDocuments → [...documents], getDocument → single document
    //           getDocumentDownloadUrl → { url: "presigned-s3-url" }
    // Side-effect: deleteDocument removes the document and all its versions.
    // Side-effect: uploadDocumentVersion creates a new version; the previous version
    //   remains accessible via version history.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: DocumentsView (main doc listing), DocumentGenerationView
    getDocuments: (linkedPartId, type, projectId, branchId, { search, limit, offset } = {}) => client.get(`${API_BASE_URL}/documents`, { params: { linked_part_id: linkedPartId, type, project_id: projectId, branch_id: branchId, search, limit, offset } }),
    // Called by: DocumentsView (download button)
    getDocumentDownloadUrl: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}/download`),
    // Called by: DocumentsView (upload new document form)
    // NOTE: Content-Type is undefined to let the browser set multipart boundary.
    createDocument: (formData) => client.post(`${API_BASE_URL}/documents`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DocumentsView (create native/body-only document without file upload)
    createNativeDocument: (projectId, title, body, docType = 'procedure', description = null) =>
      client.post(`${API_BASE_URL}/documents/native`, { project_id: projectId, title, body, type: docType, description }),
    // Called by: DocumentsView (edit document metadata), CADModelsView (update description)
    updateDocument: (documentId, docData, branchId) => client.put(`${API_BASE_URL}/documents/${documentId}`, docData, { params: { branch_id: branchId } }),
    getDocumentBodyDiff: (documentId) => client.get(`${API_BASE_URL}/documents/${documentId}/body-diff`),
    getRequirementImpact: (reqId) => client.get(`${API_BASE_URL}/requirements/${reqId}/impact`),
    // Called by: DocumentsView, CADModelsView, UnifiedCADView (upload new version of existing doc)
    uploadDocumentVersion: (documentId, formData) => client.post(`${API_BASE_URL}/documents/${documentId}/versions`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DocumentsView (delete document action)
    deleteDocument: (documentId) => client.delete(`${API_BASE_URL}/documents/${documentId}`),
    // Called by: DocumentsView (diff tab — AI comparison of two documents)
    diffDocuments: (documentIdA, documentIdB) => client.post(`${API_BASE_URL}/documents/diff`, { document_id_a: documentIdA, document_id_b: documentIdB }, { timeout: 120000 }),
    // Called by: FileDetailDrawer (compare tab — version-to-version comparison)
    compareDocumentVersions: (v1Id, v2Id) => client.post(`${API_BASE_URL}/documents/versions/compare`, { version1_id: v1Id, version2_id: v2Id }, { timeout: 60000 }),
    // Called by: UnifiedCADView (fallback for CAD drawings not in documents table)
    deleteDrawing: (drawingId) => client.delete(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    // Called by: delete confirmation dialogs — preview linked records before deletion
    getDeletePreview: (entityType, entityId) => client.get(`${API_BASE_URL}/processing/delete-preview/${entityType}/${entityId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // REQUIREMENTS
    // Response: getRequirements → [...requirements]
    //           getRequirementDownloadUrl → { url: "presigned-s3-url" }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: RequirementsView (requirements listing, filtered by linked part)
    getRequirements: (linkedPartId, status, { search, limit, offset, project_id, branch_id } = {}) => client.get(`${API_BASE_URL}/requirements`, { params: { linked_part_id: linkedPartId, status, search, limit, offset, project_id, branch_id } }),
    // Called by: RequirementsView (create requirement with attachment)
    createRequirement: (formData) => client.post(`${API_BASE_URL}/requirements`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in RequirementsView creates a Requirement per file)
    createRequirementFromFile: (formData) => client.post(`${API_BASE_URL}/requirements`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: RequirementsView (edit requirement inline)
    updateRequirement: (reqId, reqData) => client.put(`${API_BASE_URL}/requirements/${reqId}`, reqData),
    // Called by: RequirementsView (delete requirement)
    deleteRequirement: (reqId) => client.delete(`${API_BASE_URL}/requirements/${reqId}`),
    // Called by: RequirementsView (download attachment)
    getRequirementDownloadUrl: (reqId) => client.get(`${API_BASE_URL}/requirements/${reqId}/download`),
    // Called by: RequirementsView (AI extraction from document)
    extractRequirementsFromDocument: (sourceFileId, projectId) => client.post(
        `${API_BASE_URL}/requirements/extract`,
        { source_file_id: sourceFileId, project_id: projectId }
    ),
    // Called by: RequirementsView (bulk import after AI extraction review)
    bulkCreateRequirements: (projectId, requirements) => client.post(
        `${API_BASE_URL}/requirements/bulk`,
        { project_id: projectId, requirements }
    ),
    // Called by: RequirementDetailDrawer (history tab)
    getRequirementVersions: (reqId) => client.get(`${API_BASE_URL}/requirements/${reqId}/versions`),
    // Called by: RequirementsView (upload new file version — increments req.version)
    uploadRequirementVersion: (reqId, formData) => client.post(`${API_BASE_URL}/requirements/${reqId}/versions`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: RequirementDetailDrawer (compare tab)
    compareRequirementVersions: (v1Id, v2Id) => client.post(`${API_BASE_URL}/requirements/versions/compare`, {
        version1_id: v1Id,
        version2_id: v2Id,
        entity_type: 'requirement'
    }),
    // Called by: RequirementsView (coverage dashboard header, verdict/coverage columns)
    getRequirementsSummary: (projectId) => client.get(`${API_BASE_URL}/requirements/summary`, { params: { project_id: projectId } }),
    // Called by: VVMatrixPanel — complete matrix over ALL requirements (not paginated) + orphan steps + stats
    getVVMatrix: (projectId) => client.get(`${API_BASE_URL}/requirements/vv-matrix`, { params: { project_id: projectId } }),
    // Called by: EntityDetailDrawer — test cases affected by a change to a requirement / clause /
    // software_unit. Pass exactly one of requirementId / clauseId / softwareUnitId.
    getAffectedTests: (projectId, { requirementId, clauseId, softwareUnitId } = {}) =>
      client.get(`${API_BASE_URL}/requirements/affected-tests`, {
        params: {
          project_id: projectId,
          requirement_id: requirementId,
          clause_id: clauseId,
          software_unit_id: softwareUnitId,
        },
      }),
    // Called by: VVMatrixPanel — audit-backed sign-off (writes DecisionJournal entry). body: { note }
    signOffRequirement: (reqId, body = {}) => client.post(`${API_BASE_URL}/requirements/${reqId}/sign-off`, body),
    // Called by: VVMatrixPanel — revoke a prior sign-off
    revokeRequirementSignOff: (reqId, body = {}) => client.post(`${API_BASE_URL}/requirements/${reqId}/revoke-sign-off`, body),
    // Called by: VVMatrixPanel — manual verdict override / waiver. body: { disposition, justification }
    overrideRequirementVerdict: (reqId, body) => client.post(`${API_BASE_URL}/requirements/${reqId}/verdict-override`, body),
    // Called by: VVMatrixPanel — clear an active verdict override
    revokeRequirementVerdictOverride: (reqId, body = {}) => client.post(`${API_BASE_URL}/requirements/${reqId}/revoke-override`, body),
    // Called by: VVMatrixPanel — manual per-cell override (Coverage/Verdict). body: { field, value, justification }
    overrideRequirementCell: (reqId, body) => client.post(`${API_BASE_URL}/requirements/${reqId}/cell-override`, body),
    // Called by: VVMatrixPanel — clear a per-cell override. body: { field }
    revokeRequirementCellOverride: (reqId, body) => client.post(`${API_BASE_URL}/requirements/${reqId}/revoke-cell-override`, body),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENTITY LINKS (Engineering Ontology)
    // Response: getEntityLinks → {links: [...], total: number}
    // ═══════════════════════════════════════════════════════════════════════════
    // Audit trace (Phase 8) — "où est vérifiée REQ-X?"
    // Returns conception_artifacts + test_executions + verdicts + cycles_detected + generated_at.
    // requirementId path arg only; projectId is derived server-side from the requirement row.
    getAuditTrace: (requirementId, { includeSuperseded = false } = {}) =>
        client.get(`${API_BASE_URL}/audit/requirement/${requirementId}`, {
            params: { include_superseded: includeSuperseded },
        }),

    // ═══════════════════════════════════════════════════════════════════════════
    // IQ / OQ / PQ qualification protocols — ISO 13485 §7.5.6 / FDA 21 CFR 820.75
    // ═══════════════════════════════════════════════════════════════════════════
    generateIQOQPQProtocol: (body) =>
        client.post(`${API_BASE_URL}/iq-oq-pq/protocols`, body),

    listIQOQPQProtocols: (projectId, { engineeringItemId, protocolType, status, limit } = {}) =>
        client.get(`${API_BASE_URL}/iq-oq-pq/protocols`, {
            params: {
                project_id: projectId,
                engineering_item_id: engineeringItemId,
                protocol_type: protocolType,
                status,
                limit,
            },
        }),

    getIQOQPQProtocol: (protocolId, projectId) =>
        client.get(`${API_BASE_URL}/iq-oq-pq/protocols/${protocolId}`, {
            params: { project_id: projectId },
        }),

    transitionIQOQPQProtocol: (protocolId, body) =>
        client.post(`${API_BASE_URL}/iq-oq-pq/protocols/${protocolId}/transition`, body),

    deleteIQOQPQProtocol: (protocolId, projectId) =>
        client.delete(`${API_BASE_URL}/iq-oq-pq/protocols/${protocolId}`, {
            params: { project_id: projectId },
        }),

    // Called by: RequirementsView, detail drawers, graph view
    getEntityLinks: (projectId, { entityId, entityType, linkType } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links`, {
            params: { entity_id: entityId, entity_type: entityType, link_type: linkType },
        }),
    // ── ECAD / Altium designs (Electronics section) ──────────────────────
    // Signed-PUT upload flow: createEcadDesign returns { design, upload:{url} };
    // the client PUTs the ZIP to upload.url. getEcadViewerUrl returns the signed
    // GET url fed to the Altium viewer's data-project-src.
    listEcadDesigns: (projectId, { includeDeleted = false, limit = 100, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ecad/designs`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getEcadDesign: (projectId, designId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}`),
    createEcadDesign: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs`, data),
    getEcadViewerUrl: (projectId, designId, { versionId } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/viewer-url`, {
            params: versionId ? { version_id: versionId } : {},
        }),
    newEcadVersion: (projectId, designId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/version`, data),
    updateEcadDesign: (projectId, designId, data) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}`, data),
    getEcadVersions: (projectId, designId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/versions`),
    restoreEcadVersion: (projectId, designId, versionId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/versions/${versionId}/restore`),
    deleteEcadDesign: (projectId, designId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}`),
    // Ingest an exported BOM CSV → components stored on the design (feeds
    // change-impact + judge subject text). Pass `undefined` Content-Type so the
    // browser sets the multipart boundary — the client default is
    // application/json, which otherwise suppresses it (see CLAUDE.md pitfall).
    uploadEcadBom: (projectId, designId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/bom`, fd, {
            headers: { 'Content-Type': undefined },
        });
    },
    // AI compliance check (mirrors CAD): enqueue against requirements/standards,
    // poll getJobStatus, then read the saved result.
    checkEcadCompliance: (projectId, designId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/check-compliance`, body),
    getEcadCompliance: (projectId, designId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/compliance`),
    // BOM enrichment (Nexar/Octopart supply + compliance data). Async job.
    enrichEcadBom: (projectId, designId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ecad/designs/${designId}/enrich-bom`),
    createEntityLink: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links`, data),
    createEntityLinksBulk: (projectId, links) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/bulk`, { links }),
    suggestEntityLinks: (projectId, { sourceTypes, targetTypes, maxSuggestions, threshold } = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entities/suggest-links`, {
            source_types: sourceTypes,
            target_types: targetTypes,
            max_suggestions: maxSuggestions,
            threshold,
        }, { timeout: 120000 }),
    suggestLinksForEntity: (projectId, entityType, entityId, { maxSuggestions = 10, threshold = 0.55 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/suggest-for/${entityType}/${entityId}`, {
            params: { max_suggestions: maxSuggestions, threshold },
            timeout: 90000,
        }),
    reclaimEntityLinks: (projectId, fromEntityId, toEntityId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/reclaim`, {
            from_entity_id: fromEntityId,
            to_entity_id: toEntityId,
        }),
    deleteEntityLink: (projectId, linkId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}`),
    getEntityTimeline: (projectId, entityId, limit = 100) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/timeline`, {
            params: { entity_id: entityId, limit },
        }),
    getEntityLinksNeighborhood: (projectId, { entity_type, entity_id, hops = 2 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/neighborhood`, {
            params: { entity_type, entity_id, hops },
        }),
    invalidateEntityLink: (projectId, linkId, { endedAt, supersededBy } = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}/invalidate`, null, {
            params: { ended_at: endedAt, superseded_by: supersededBy },
        }),

    // Confirm-queue (Phase D) — AI-suggested links awaiting human judgment.
    getPendingLinks: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/pending`),
    confirmLink: (projectId, linkId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}/confirm`),
    rejectLink: (projectId, linkId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/${linkId}/reject`),
    decideLinksBatch: (projectId, action, linkIds) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entity-links/decide-batch`, { link_ids: linkIds }, {
            params: { action },
        }),
    getLinkPrecisionStats: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entity-links/precision-stats`),

    // Altium / generic BOM CSV import (Phase C) — multipart upload → Part nodes.
    importEcadBom: (projectId, file, { mappings } = {}) => {
        const form = new FormData();
        form.append('file', file);
        if (mappings) form.append('mappings', JSON.stringify(mappings));
        return client.post(`${API_BASE_URL}/projects/${projectId}/ecad/bom-import`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000,
        });
    },

    // Bulk resolver — turns [(entity_type, entity_id), ...] into display labels.
    // Used by KnowledgeGraphView so nodes don't render as raw UUIDs.
    // Response: {items: [{type, id, label, deleted, status}, ...]}
    resolveEntities: (projectId, items) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/entities/resolve`, { items }),

    // Returns downstream impact subgraph for a changed entity. Used by ImpactBanner + ImpactGraphDialog.
    getEntityImpactGraph: (projectId, entityType, entityId, maxHops = 3) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/entities/${entityType}/${entityId}/impact-graph`, {
            params: { max_hops: maxHops },
        }),

    // Returns {"entity_type::entity_id": stale_count} for the whole project.
    // One request per list-view load; list rows filter by their own entity key.
    getStaleVerdictCounts: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/stale-verdict-counts`),

    // Returns {"entity_type::entity_id": "stale"|"red"|"amber"} for every judged
    // entity with a non-clean state. Consumed by the KG view to paint persistent
    // compliance-status badges regardless of navigation path.
    getComplianceStatus: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/compliance-status`),

    // ── Change records (change-impact anchors) ──
    listChangeRecords: (projectId, { status, impact, risk, trigger, q, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/change-records`, { params: { status, impact, risk, trigger, q, limit, offset } }),
    exportChangeRecords: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/change-records/export`, { responseType: 'blob' }),
    getChangeRecord: (changeId) =>
        client.get(`${API_BASE_URL}/change-records/${changeId}`),
    createChangeRecord: (projectId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/change-records`, body),
    patchChangeRecord: (changeId, body) =>
        client.patch(`${API_BASE_URL}/change-records/${changeId}`, body),
    recheckChangeRecord: (changeId) =>
        client.post(`${API_BASE_URL}/change-records/${changeId}/recheck`),
    addChangeRecordAffected: (changeId, body) =>
        client.post(`${API_BASE_URL}/change-records/${changeId}/affected`, body),
    removeChangeRecordAffected: (changeId, entityType, entityId) =>
        client.delete(`${API_BASE_URL}/change-records/${changeId}/affected/${entityType}/${encodeURIComponent(entityId)}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // DECISION JOURNAL
    // ═══════════════════════════════════════════════════════════════════════════
    getDecisions: (projectId, { entityId, decisionType, tag, status, limit, offset } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/decisions`, {
            params: { entity_id: entityId, decision_type: decisionType, tag, status, limit, offset },
        }),
    getDecision: (projectId, entryId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/decisions/${entryId}`),
    createDecision: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/decisions`, data),
    deleteDecision: (projectId, entryId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/decisions/${entryId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // APPROVALS / Submit-for-Review (Phase 0/1)
    // Response: submitForReview → ApprovalResponse; listApprovals → {approvals, total}
    // ═══════════════════════════════════════════════════════════════════════════
    submitForReview: (entityType, entityId, projectId, reviewers, comment) =>
        client.post(`${API_BASE_URL}/approvals`, {
            entity_type: entityType,
            entity_id: entityId,
            project_id: projectId,
            reviewers,
            comment,
        }),
    listApprovals: ({ projectId, status, assignee, entityType, entityId, limit, offset } = {}) =>
        client.get(`${API_BASE_URL}/approvals`, {
            params: {
                project_id: projectId,
                status,
                assignee,
                entity_type: entityType,
                entity_id: entityId,
                limit,
                offset,
            },
        }),
    getApproval: (approvalId) =>
        client.get(`${API_BASE_URL}/approvals/${approvalId}`),
    decideApproval: (approvalId, decision, comment) =>
        client.post(`${API_BASE_URL}/approvals/${approvalId}/decide`, { decision, comment }),

    // ═══════════════════════════════════════════════════════════════════════════
    // NOTIFICATIONS (Phase 0/1)
    // ═══════════════════════════════════════════════════════════════════════════
    getNotifications: ({ limit, offset, onlyUnread } = {}) =>
        client.get(`${API_BASE_URL}/notifications`, {
            params: { limit, offset, only_unread: onlyUnread },
        }),
    getUnreadNotificationCount: () =>
        client.get(`${API_BASE_URL}/notifications/unread-count`),
    markNotificationsRead: (sourceType, upTo) =>
        client.post(`${API_BASE_URL}/notifications/mark-read`, {
            source_type: sourceType,
            up_to: upTo,
        }),

    // ═══════════════════════════════════════════════════════════════════════════
    // GLOBAL SEARCH — GET /api/v1/search/global
    // Note: distinct from the older POST /api/v1/search (KB-vector retrieval).
    // Response: { results: [...hits], total: int, truncated: bool }
    // ═══════════════════════════════════════════════════════════════════════════
    globalSearch: ({ q, types, projectId, limit, offset } = {}) =>
        client.get(`${API_BASE_URL}/search/global`, {
            params: {
                q,
                types: Array.isArray(types) ? types.join(',') : types,
                project_id: projectId,
                limit,
                offset,
            },
        }),

    // ═══════════════════════════════════════════════════════════════════════════
    // RECENTLY DELETED (Soft-delete recovery)
    // ═══════════════════════════════════════════════════════════════════════════
    getRecentlyDeleted: (projectId) =>
        client.get(`${API_BASE_URL}/processing/recently-deleted/${projectId}`),
    restoreEntity: (entityType, entityId) =>
        client.post(`${API_BASE_URL}/processing/restore/${entityType}/${entityId}`),
    permanentlyDeleteEntity: (entityType, entityId) =>
        client.delete(`${API_BASE_URL}/processing/permanently-delete/${entityType}/${entityId}`),
    checkDuplicates: (filenames, projectId, branchId, entityType = 'document') =>
        client.post(`${API_BASE_URL}/files/check-duplicates`, { filenames, project_id: projectId, branch_id: branchId, entity_type: entityType }),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENGINEERING MANAGEMENT (Risks, Design Gates, Test Cases)
    // ═══════════════════════════════════════════════════════════════════════════
    getRisks: (projectId, status) => client.get(`${API_BASE_URL}/projects/${projectId}/risks`, { params: { status } }),
    createRisk: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/risks`, data),
    updateRisk: (projectId, riskId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/risks/${riskId}`, data),
    deleteRisk: (projectId, riskId) => client.delete(`${API_BASE_URL}/projects/${projectId}/risks/${riskId}`),

    getDesignGates: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/design-gates`),
    updateDesignGate: (projectId, gateId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/design-gates/${gateId}`, data),

    getTestCases: (projectId, { status, linked_requirement_id } = {}) => client.get(`${API_BASE_URL}/projects/${projectId}/test-cases`, { params: { status, linked_requirement_id } }),
    createTestCase: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/test-cases`, data),
    updateTestCase: (projectId, testId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/test-cases/${testId}`, data),
    deleteTestCase: (projectId, testId) => client.delete(`${API_BASE_URL}/projects/${projectId}/test-cases/${testId}`),

    // Inspection Checklists
    getInspectionChecklists: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/inspection-checklists`),
    updateInspectionChecklist: (projectId, checklistId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/inspection-checklists/${checklistId}`, data),
    deleteInspectionChecklist: (projectId, checklistId) => client.delete(`${API_BASE_URL}/projects/${projectId}/inspection-checklists/${checklistId}`),

    // Verification Matrix
    getLatestVerificationMatrix: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/verification-matrix/latest`),

    // AI Verification
    aiGenerateTestCases: (projectId, requirementIds) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/generate-test-cases`, { requirement_ids: requirementIds }),
    aiGenerateInspectionChecklist: (projectId, drawingId, requirementIds) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/generate-inspection-checklist`, { drawing_id: drawingId, requirement_ids: requirementIds }),
    aiAutoMapMatrix: (projectId) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/auto-map-matrix`),
    aiAnalyzeFailure: (projectId, testId) => client.post(`${API_BASE_URL}/projects/${projectId}/ai/analyze-failure`, { test_id: testId }),

    // ═══════════════════════════════════════════════════════════════════════════
    // HISTORY
    // Response: getHistory → [...activity entries] for the project
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ActivityFeed (project-level activity timeline)
    getHistory: (projectId) => client.get(`${API_BASE_URL}/product-structure/history`, { params: { project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // ENGINEERING (AI-powered analysis)
    // Response: analyzeDesign → { analysis: "..." }, generateSpecs → { specs: "..." }
    //           processDWG → { extracted_data: {...} }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (not directly referenced in current components — backend-triggered or future UI)
    analyzeDesign: (data) => client.post(`${API_BASE_URL}/engineering/analyze`, data),
    generateSpecs: (data) => client.post(`${API_BASE_URL}/engineering/generate-specs`, data),
    processDWG: (filePath, s3Url) => client.post(`${API_BASE_URL}/engineering/process-dwg`, { file_path: filePath, s3_url: s3Url }),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD AGENT (Autodesk Platform Services integration)
    // Response: getCADViewerToken → { access_token, expires_in }
    //           getCADDrawings → [...drawings], getCADDrawing → single drawing
    //           getCADJobStatus → { status, progress, ... }
    //           translateCADDrawing → { job_id, ... }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (CAD viewer token is fetched by APSViewer, CADViewerView via client directly)
    getCADViewerToken: () => client.get(`${API_BASE_URL}/cad-agent/viewer/token`),
    // Called by: CADModelsView, UnifiedCADView (drawing list for a project)
    getCADDrawings: (projectId) => client.get(`${API_BASE_URL}/cad-agent/drawings`, { params: { project_id: projectId } }),
    getCADDrawing: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    createCADDrawing: (data) => client.post(`${API_BASE_URL}/cad-agent/drawings`, data),
    deleteCADDrawing: (drawingId) => client.delete(`${API_BASE_URL}/cad-agent/drawings/${drawingId}`),
    getCADJobStatus: (jobId) => client.get(`${API_BASE_URL}/cad-agent/jobs/${jobId}/status`),
    getCADDrawingJobs: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/jobs`),
    translateCADDrawing: (drawingId) => client.post(`${API_BASE_URL}/cad-agent/translate/${drawingId}`),
    getCADTranslationStatus: (drawingId) => client.get(`${API_BASE_URL}/cad-agent/translate/${drawingId}/status`),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD IMPORT — import an existing document into the CAD viewer
    // Side-effect: creates a CAD drawing record linked to the document and
    //   triggers a translation job. The document must already exist.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADModelsView, UnifiedCADView (import action on a document row)
    importDocumentToCAD: (documentId, versionId) => client.post(`${API_BASE_URL}/cad-agent/import/from-document/${documentId}`, null, { params: versionId ? { version_id: versionId } : {} }),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT ANALYSIS WITH AI
    // Can analyze either a document (pass documentId) or a requirement attachment (pass requirementId).
    // Response: { analysis: "...", analysis_type: "summary"|"risks"|"custom"|... }
    // Timeout: 120s (overrides client default — same value, but explicit).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: RequirementsView (AI analysis of requirement attachments)
    analyzeDocument: ({ documentId, requirementId, analysisType = 'summary', language = 'en', customPrompt = null }) => client.post(
        `${API_BASE_URL}/documents/analyze`,
        {
            document_id: documentId || null,
            requirement_id: requirementId || null,
            analysis_type: analysisType,
            language: language,
            custom_prompt: customPrompt
        },
        { timeout: 30000 }  // Returns 202 immediately; frontend polls for result
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // ORCHESTRATOR (engineering lifecycle pipeline builder)
    // ═══════════════════════════════════════════════════════════════════════════
    getUserProfile: () => client.get(`${API_BASE_URL}/auth/me`),
    completeOnboarding: () => client.put(`${API_BASE_URL}/auth/me`, { onboarding_completed: true }),
    updateOnboardingProgress: (progress) => client.put(`${API_BASE_URL}/auth/me`, { onboarding_progress: progress }),
    getLifecycleStages: () => client.get(`${API_BASE_URL}/orchestrator/stages`),
    getStageTools: (stageId) => client.get(`${API_BASE_URL}/orchestrator/stages/${stageId}/tools`),
    getOrchestrationStatus: (runId) => client.get(`${API_BASE_URL}/orchestrator/status/${runId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // DOCUMENT GENERATION (UC1 — AI-generated engineering documents)
    // Response: generateDocument → { generated_doc_id, status: "generating" } (fire-and-forget)
    //           getPendingDocumentReviews → [...pending reviews]
    //           getGeneratedDocument → single generated document with content
    //           submitDocumentReview → { status: "approved"|"rejected" }
    //           getDocumentTemplates → [...templates]
    // Side-effect: submitDocumentReview with approval auto-creates a document record.
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: DocumentsView (generate document action)
    generateDocument: (data) => client.post(`${API_BASE_URL}/document-generation/generate`, data, { timeout: 60000 }),
    // Called by: DocumentsView, DocumentGenerationView (pending reviews panel)
    getPendingDocumentReviews: (projectId) => client.get(
        `${API_BASE_URL}/document-generation/pending`,
        { params: projectId ? { project_id: projectId } : {} }
    ),
    // Called by: DocumentsView, DocumentGenerationView (load generated doc content for review)
    getGeneratedDocument: (id) => client.get(`${API_BASE_URL}/document-generation/${id}`),
    // Called by: DocumentsView, DocumentGenerationView (approve/reject/request-changes)
    submitDocumentReview: (id, review) => client.post(`${API_BASE_URL}/document-generation/${id}/review`, review),
    // Called by: (change impact analysis between document versions)
    analyzeChangeImpact: (sourceId, oldVersion, newVersion) => client.get(
        `${API_BASE_URL}/document-generation/impact/${sourceId}`,
        { params: { old_version_id: oldVersion, new_version_id: newVersion } }
    ),
    // Called by: DocumentsView, DocumentGenerationView (template picker in generate dialog)
    getDocumentTemplates: (type) => client.get(
        `${API_BASE_URL}/document-generation/templates`,
        { params: type ? { document_type: type } : {} }
    ),
    // Called by: (admin template management — not directly referenced in current components)
    createDocumentTemplate: (template) => client.post(`${API_BASE_URL}/document-generation/templates`, template),

    // ═══════════════════════════════════════════════════════════════════════════
    // REPORTS (AI-Generated Documents — read-only listing)
    // Response: getReports → [...reports], getLinkedReports → [...reports linked to source]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ReportsView (report listing by project/branch)
    getReports: (projectId, branchId) => client.get(
        `${API_BASE_URL}/document-generation/reports`,
        { params: { project_id: projectId, branch_id: branchId } }
    ),
    // Called by: FileDetailDrawer (shows reports linked to a source document)
    getLinkedReports: (sourceDocumentId) => client.get(
        `${API_BASE_URL}/document-generation/reports/linked/${sourceDocumentId}`
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // ACTIVITY LOGS
    // Response: getActivities → [...activity log entries]
    //           getProjectActivities → [...activity log entries for a project]
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ActivityContext (global activity polling)
    getActivities: (limit = 50, resourceType = null, action = null, search = null) => client.get(
        `${API_BASE_URL}/activities`,
        { params: { limit, resource_type: resourceType, action, search } }
    ),
    // Called by: ActivityFeed (project-scoped activity feed with search)
    getProjectActivities: (projectId, limit = 50, resourceType = null, action = null, search = null) => client.get(
        `${API_BASE_URL}/projects/${projectId}/activities`,
        { params: { limit, resource_type: resourceType, action, search } }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // CAD EXPORT (DWG → PDF/DWF via APS Model Derivative)
    // Response: exportDrawing → { status: "started", format, drawing_id }
    //           getExportStatus → { status: "complete"|"inprogress"|"failed", download_url?, filename? }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADViewerArea (Export button)
    exportDrawing: (drawingId, format) => client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/export`, { format }),
    getExportStatus: (drawingId, format) => client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/export/status`, { params: { format } }),
    // Called by: ComplianceView Dashboard (summary cards without loading all drawings)
    getDrawingsSummary: (projectId) => client.get(`${API_BASE_URL}/cad-agent/drawings/summary`, { params: { project_id: projectId } }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT MILESTONES
    // Response: getMilestones → [...milestones], createMilestone → milestone object
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: MilestonesPanel, AnalyticsView
    getMilestones: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/milestones`),
    createMilestone: (projectId, data) => client.post(`${API_BASE_URL}/projects/${projectId}/milestones`, data),
    updateMilestone: (projectId, milestoneId, data) => client.put(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`, data),
    deleteMilestone: (projectId, milestoneId) => client.delete(`${API_BASE_URL}/projects/${projectId}/milestones/${milestoneId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT ANALYTICS
    // Response: getProjectBrief → { project, requirements, documents, ... }
    //           getProjectProgression → { commits_per_week, doc_versions_per_week, ... }
    //           getProjectRisks → eQMS risks: { failed_requirements, overdue_capas,
    //             failed_vv_sessions, red_verdicts, stale_verdicts, unsigned_docs, unmitigated_hazards }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: AnalyticsView (Project Brief tab)
    getProjectBrief: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/brief`),
    // Called by: AnalyticsView (Progression tab)
    getProjectProgression: (projectId, weeks = 12) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/progression`,
        { params: { weeks } }
    ),
    // Called by: AnalyticsView (Risks tab)
    getProjectRisks: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/risks`),
    // Called by: AnalyticsView (Scorecard tab)
    getProjectScorecard: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/scorecard`),
    // Called by: AnalyticsView (Intelligence tab)
    getEngineeringIntelligence: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/intelligence`),
    // Called by: AnalyticsView (AI Health Summary tab)
    getProjectHealthSummary: (projectId, force = false) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/health-summary`,
        { params: { force } }
    ),
    // Called by: AnalyticsView (Gate Review Report)
    getGateReviewReport: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/analytics/gate-review`),
    exportGateReviewReport: (projectId) => client.get(
        `${API_BASE_URL}/projects/${projectId}/analytics/gate-review`,
        { params: { format: 'xlsx' }, responseType: 'blob' }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // BOM EXTRACTION (AI-powered Bill of Materials from CAD drawings)
    // Response: saveBOM → { message, ... }, exportBOM → Blob (file download)
    // extractBOM: starts async BOM extraction, returns 202 + job_id
    extractBOM: (drawingId) => client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/extract-bom`),
    // whatChanged: AI summary of differences between two file versions of the
    // source Document. Called by: WhatChangedDialog (from CAD ribbon).
    // body: { from_version_id?, to_version_id? } — both optional; defaults to
    // latest 2 versions of the drawing's source Document.
    whatChanged: (drawingId, fromVersionId, toVersionId) =>
        client.post(
            `${API_BASE_URL}/cad-agent/drawings/${drawingId}/what-changed`,
            { from_version_id: fromVersionId, to_version_id: toVersionId },
            { timeout: 60000 },
        ),
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: BOMReviewDialog (save user-edited BOM back to DB)
    // opts: { run_id?: string, consent_to_training?: boolean }
    // When run_id is the latest_run_id from the blob, backend writes a BOMReview
    // + per-row correction events (Phase 2 training signal). Without it, save still
    // works via the legacy blob-overwrite path.
    saveBOM: (drawingId, bomItems, opts = {}) => client.put(
        `/api/v1/cad-agent/drawings/${drawingId}/bom`,
        {
            bom_items: bomItems,
            run_id: opts.run_id ?? null,
            consent_to_training: opts.consent_to_training ?? false,
        }
    ),
    // Called by: BOMReviewDialog (download Excel/CSV export)
    exportBOM: (drawingId, format = 'xlsx') => client.get(
        `/api/v1/cad-agent/drawings/${drawingId}/bom/export`,
        { params: { format }, responseType: 'blob' }
    ),
    // Called by: BOMReviewDialog (retrieve previously extracted BOM)
    getBOM: (drawingId) => client.get(`/api/v1/cad-agent/drawings/${drawingId}/bom`),

    // M9 — user override of the auto-classified engineering drawing type.
    // Body: { engineering_drawing_type: "assembly" | "detail" | "pid" | ... }
    // Each correction feeds the M2 flywheel; future re-extractions route to the
    // chosen prompt.
    updateDrawingEngineeringType: (drawingId, engineeringDrawingType) =>
        client.patch(
            `/api/v1/cad-agent/drawings/${drawingId}/engineering-type`,
            { engineering_drawing_type: engineeringDrawingType }
        ),

    // M14 — PMI extraction (paired with M1). Charges 2 credits.
    // Synchronous: returns the extracted PMI items inline so the reviewer can
    // open the PMI tab immediately without polling.
    extractPMI: (drawingId) => client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/extract-pmi`),
    // Read a single PMI run by id (audit replay, time-travel debugging).
    getPMIRun: (drawingId, runId) =>
        client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/pmi-runs/${runId}`),
    // List recent PMI runs newest-first; tab uses this to load latest without re-extracting.
    listPMIRuns: (drawingId, limit = 20) =>
        client.get(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/pmi-runs`, { params: { limit } }),
    // Persist a user review of a PMI run — diffs against ai_items, writes
    // PMIReview + PMICorrectionEvent rows for the M2 correction flywheel.
    savePMIReview: (drawingId, runId, body) =>
        client.post(
            `${API_BASE_URL}/cad-agent/drawings/${drawingId}/pmi-runs/${runId}/review`,
            body,
        ),

    // ═══════════════════════════════════════════════════════════════════════════
    // SUPPLIER MATCHING (BOM lines → supplier candidates + cost rollup)
    // Lifecycle:
    //   1. createBOMFromDrawing(projectId, drawingId) — once per drawing,
    //      reuses on re-call (same bom_id back).
    //   2. matchSuppliers(projectId, bomId, body) — async, 202 + job_id.
    //      Watch progress via JobPollerContext (job_type="supplier_matching").
    //   3. getBOMMatches — list lines + cached matches.
    //   4. decideSupplierMatch — accept/reject (idempotent on match_id).
    //   5. getCostRollup — total + per-line breakdown, optional currency.
    // Per-line billing: 4 credits per first match, 2 per forced re-match.
    // 402 surfaces globally via the existing axios interceptor + paywall.
    // ═══════════════════════════════════════════════════════════════════════════
    createBOMFromDrawing: (projectId, drawingId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/from-drawing/${drawingId}`),
    createBOMFromDocument: (projectId, file, documentId) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_id', documentId);
        // Pass `undefined` Content-Type so the browser sets multipart boundary
        // automatically — see CLAUDE.md "multipart uploads" pitfall.
        return client.post(
            `${API_BASE_URL}/projects/${projectId}/bom/from-document`,
            formData,
            { headers: { 'Content-Type': undefined } }
        );
    },
    // Upload an XLSX/CSV BOM directly to a drawing — overrides any extracted
    // or previously-uploaded BOM for that drawing. Used when AI extraction
    // is wrong/incomplete and the engineer has the canonical spreadsheet.
    uploadBOMToDrawing: (projectId, drawingId, file, opts = {}) => {
        const formData = new FormData();
        formData.append('file', file);
        const params = {};
        if (opts.sheetName) params.sheet_name = opts.sheetName;
        // Option-C — bypass the deterministic parser, use Bedrock directly.
        // Costs 3 credits. Wired through the sheet-picker dialog's
        // "Force AI parsing" checkbox.
        if (opts.forceAi) params.force_ai = true;
        return client.post(
            `${API_BASE_URL}/projects/${projectId}/bom/upload-to-drawing/${drawingId}`,
            formData,
            { headers: { 'Content-Type': undefined }, params }
        );
    },
    // Re-run AI on an existing upload's archived bytes. No re-upload
    // needed; requires the BOM to have an `upload_id` (i.e. it was
    // uploaded post-Option-C and bytes were stored). Returns 400 if not.
    // Charges 3 credits per call (every invocation is a fresh Bedrock hit).
    reparseBOMWithAi: (projectId, bomId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/re-parse-ai`),
    // Sheet picker — preview an XLSX workbook before commit. Returns
    //   { sheets: [{name, row_count, header_match_count, preview: [[...]]}],
    //     auto_pick: <name> }
    // The frontend skips the picker dialog and calls uploadBOMToDrawing
    // directly when `sheets.length === 1` so single-sheet workbooks add
    // no friction.
    inspectBomXlsx: (projectId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return client.post(
            `${API_BASE_URL}/projects/${projectId}/bom/inspect-xlsx`,
            formData,
            { headers: { 'Content-Type': undefined } }
        );
    },
    matchSuppliers: (projectId, bomId, body = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/match-suppliers`, body),
    getBOMMatches: (projectId, bomId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/matches`),
    // PR 3: inline-edit a single BOMLine from the reconciliation panel.
    // Body is partial — only present fields update. Stamps
    // value_provenance['<field>'] = 'typed' on every edited field so the
    // FieldPicker treats it as deliberate human input.
    updateBOMLine: (projectId, lineId, body) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/bom-lines/${lineId}`, body),
    // Remove a line (and its supplier matches + decisions) from the working BOM.
    deleteBOMLine: (projectId, lineId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/bom-lines/${lineId}`),
    // Add an operator-authored line to a drawing's manual BOM (creates the
    // manual BOM on first call). Used to source a single part with no extraction.
    addBOMLine: (projectId, drawingId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/from-drawing/${drawingId}/lines`, body),
    // M13: tree-shaped BOM (3D-extracted only). Returns {tree: []} for 2D BOMs.
    getBOMAssemblyTree: (projectId, bomId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/assembly-tree`),
    // M13: kick off APS objectTree → flat BOM extraction for a 3D drawing.
    extractBOM3D: (drawingId) =>
        client.post(`${API_BASE_URL}/cad-agent/drawings/${drawingId}/extract-bom-3d`),
    decideSupplierMatch: (projectId, bomId, lineId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/lines/${lineId}/decide`, body),
    // Operator-entered supplier the AI didn't find. `is_manual` matches are
    // never wiped by a re-match. Only supplier_name is required.
    addManualSupplierMatch: (projectId, bomId, lineId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/lines/${lineId}/manual-match`, body),
    getCostRollup: (projectId, bomId, currency = 'EUR') =>
        client.get(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/cost-rollup`, { params: { currency } }),
    // Download the sourcing workbook (.xlsx) — summary + accepted/rejected audit.
    exportSuppliers: (projectId, bomId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/bom/${bomId}/export.xlsx`, { responseType: 'blob' }),

    // ═══════════════════════════════════════════════════════════════════════════
    // M16 / M17 — RECONCILIATION + ENGINEERING ITEM GROUPING
    // ═══════════════════════════════════════════════════════════════════════════
    // Resolve (or create) the EngineeringItem for a drawing.
    ensureEngineeringItemFromDrawing: (projectId, drawingId) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/from-drawing/${drawingId}`,
        ),
    // Item detail — includes `links` and (post-this-PR) `grouped_boms`.
    getEngineeringItem: (projectId, itemId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}`),
    // BOMs in the project NOT currently grouped under this item — picker fuel.
    getGroupableBOMs: (projectId, itemId) =>
        client.get(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/groupable-boms`,
        ),
    // Attach an existing BOM (or any artifact) to this item. Wraps relink so
    // any prior `groups` link for the BOM is invalidated atomically.
    attachBOMToItem: (projectId, itemId, bomId) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/relink`,
            { artifact_id: bomId, artifact_kind: 'bom' },
        ),
    // Project-wide list of EngineeringItems — used by the manual-link picker.
    listEngineeringItems: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/engineering-items`),
    // Generic relink: move any artifact (drawing/bom/document) to a different
    // EngineeringItem. Existing `groups` link is invalidated atomically.
    relinkArtifact: (projectId, itemId, artifactId, artifactKind) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/relink`,
            { artifact_id: artifactId, artifact_kind: artifactKind },
        ),
    // Update a drawing's canonical identity (part_number / description) and
    // re-resolve its EngineeringItem grouping. The fix-it-after path for files
    // dragged in without a PN.
    updateDrawingCanonicalIdentity: (projectId, drawingId, partNumber, description) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/cad-drawings/${drawingId}/canonical-identity`,
            { part_number: partNumber, description: description },
        ),
    // (invalidateEntityLink defined earlier at line ~572 with richer
    // {endedAt, supersededBy} params — used here by the Reconciliation
    // panel's "Detach" button with just (projectId, linkId), which is
    // backward-compatible since the extra params default to undefined.)
    // Trigger reconciliation run (5 credits).
    runReconciliation: (projectId, itemId) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/reconcile`,
        ),
    listReconciliationRuns: (projectId, itemId, limit = 1) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/reconciliation-runs`, {
            params: { engineering_item_id: itemId, limit },
        }),
    getReconciliationRun: (projectId, runId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/reconciliation-runs/${runId}`),
    // Explains why the unified BOM has the row count it does — per grouped
    // BOM, total lines vs distinct canonical_keys, plus the duplicate-PN
    // collapse map and "no identity" drop count. Reads live data; does NOT
    // consume credits.
    getReconciliationDiagnostics: (projectId, itemId) =>
        client.get(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/reconciliation-diagnostics`,
        ),
    // M16+ truth-finding overhaul. Resolutions persist engineer decisions at
    // (engineering_item, canonical_key, field) granularity. The reconciliation
    // engine reads active resolutions before emitting findings — when valid
    // they're silently applied; when stale they surface as resolution_stale.
    createReconciliationResolution: (projectId, itemId, body) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/reconciliation-resolutions`,
            body,
        ),
    listReconciliationResolutions: (projectId, itemId) =>
        client.get(
            `${API_BASE_URL}/projects/${projectId}/engineering-items/${itemId}/reconciliation-resolutions`,
        ),

    // ═══════════════════════════════════════════════════════════════════════════
    // BUCKET B CONFORMANCE ENGINE (compliance reconciliation)
    // Runs deterministic conformance checks (PL vs PLr, clause evidence
    // coverage, DoC vs evidence, requirement vs verification result). Free — no credits.
    // ═══════════════════════════════════════════════════════════════════════════
    // body: { check_types?: string[], subject_id?: string }
    runConformanceChecks: (projectId, body = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/conformance/run`, body),
    getConformanceRun: (projectId, runId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/conformance/runs/${runId}`),
    listConformanceRuns: (projectId, { limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/conformance/runs`, {
            params: { limit, offset },
        }),
    // filters: { status?, checkType?, severity? }
    listConformanceFindings: (projectId, { status, checkType, severity, limit = 100, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/conformance/findings`, {
            params: { status, check_type: checkType, severity, limit, offset },
        }),
    // body: { status, note? }
    updateConformanceFinding: (projectId, findingId, body) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/conformance/findings/${findingId}`, body),

    // ═══════════════════════════════════════════════════════════════════════════
    // COMPLIANCE CHECKING
    // checkCompliance uses native fetch() for NDJSON streaming — NOT Axios.
    //   The caller handles streaming (see CADFileDetailDrawer Compliance tab).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: CADFileDetailDrawer (retrieve saved compliance result)
    getCompliance: (drawingId) => client.get(`/api/v1/cad-agent/drawings/${drawingId}/compliance`),
    // Called by: ComplianceView Dashboard (manual pass/fail/warning override)
    setManualComplianceStatus: (drawingId, status) =>
        client.patch(`/api/v1/cad-agent/drawings/${drawingId}/compliance-status`, { status }),
    // Called by: ComplianceView Dashboard (assign requirements/standards to a drawing)
    setComplianceAssigned: (drawingId, requirementIds, standardIds) =>
        client.patch(`/api/v1/cad-agent/drawings/${drawingId}/compliance-assigned`, {
            requirement_ids: requirementIds, standard_ids: standardIds,
        }),
    // Called by: CADFileDetailDrawer (download Excel/PDF export)
    exportCompliance: (drawingId, format = 'xlsx') => client.get(
        `/api/v1/cad-agent/drawings/${drawingId}/compliance/export`,
        { params: { format }, responseType: 'blob' }
    ),

    // ═══════════════════════════════════════════════════════════════════════════
    // COMPLIANCE STANDARDS
    // ═══════════════════════════════════════════════════════════════════════════
    getComplianceStandards: (projectId, { search, limit, offset, branch_id } = {}) => client.get(`${API_BASE_URL}/compliance/standards`, { params: { project_id: projectId, branch_id, search, limit, offset } }),
    createComplianceStandard: (formData) => client.post(`${API_BASE_URL}/compliance/standards`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in ComplianceView creates a ComplianceStandard per file)
    createComplianceStandardFromFile: (formData) => client.post(`${API_BASE_URL}/compliance/standards`, formData, { headers: { 'Content-Type': undefined } }),
    // Called by: DropZoneUploader (drag-drop in CAD view — uses the regular file upload which auto-categorizes as 'cad')
    uploadCADDrawing: (formData) => client.post(`${API_BASE_URL}/files/upload`, formData, { headers: { 'Content-Type': undefined } }),
    updateComplianceStandard: (standardId, data) => client.put(`${API_BASE_URL}/compliance/standards/${standardId}`, data),
    deleteComplianceStandard: (standardId) => client.delete(`${API_BASE_URL}/compliance/standards/${standardId}`),
    getComplianceStandardDownloadUrl: (standardId) => client.get(`${API_BASE_URL}/compliance/standards/${standardId}/download`),
    // Called by: ComplianceStandardDetailDrawer (history tab)
    getStandardVersions: (standardId) => client.get(`${API_BASE_URL}/compliance/standards/${standardId}/versions`),
    // ── Standard Clauses (Track A — public clause numbers + titles + tenant annotations) ──
    listStandardClauses: (standardId, { limit, offset, includeInvalidated } = {}) =>
      client.get(`${API_BASE_URL}/standards/${standardId}/clauses`, {
        params: { limit, offset, include_invalidated: includeInvalidated },
      }),
    getStandardClause: (standardId, clauseId) =>
      client.get(`${API_BASE_URL}/standards/${standardId}/clauses/${clauseId}`),
    createStandardClause: (standardId, payload) =>
      client.post(`${API_BASE_URL}/standards/${standardId}/clauses`, payload),
    bulkLoadStandardClauses: (standardId, items) =>
      client.post(`${API_BASE_URL}/standards/${standardId}/clauses/bulk`, { items }),
    updateClauseAnnotation: (standardId, clauseId, tenantAnnotation) =>
      client.patch(`${API_BASE_URL}/standards/${standardId}/clauses/${clauseId}/annotation`, {
        tenant_annotation: tenantAnnotation,
      }),
    // ── LLM-as-judge / continuous compliance (Track C.1 + C.2) ──
    // Run the judge on one (entity, clause) pair. Cache-aware unless force=true.
    // Called by: ComplianceStatusView (Compliance → Compliance Status tab)
    requestJudge: ({ entityType, entityId, clauseId, force = false } = {}) =>
      client.post(`${API_BASE_URL}/judge`, {
        entity_type: entityType,
        entity_id: entityId,
        clause_id: clauseId,
        force,
      }),
    // List cached verdicts. Excludes superseded rows unless includeSuperseded=true.
    // Pass projectId for the project-wide Compliance Status snapshot.
    getVerdicts: ({ projectId, entityType, entityId, clauseId, status, includeSuperseded = false, limit, offset } = {}) =>
      client.get(`${API_BASE_URL}/judge/verdicts`, {
        params: {
          project_id: projectId,
          entity_type: entityType,
          entity_id: entityId,
          clause_id: clauseId,
          status,
          include_superseded: includeSuperseded,
          limit,
          offset,
        },
      }),
    // Seed the Compliance Status snapshot: enqueue judgments for every
    // (entity, active clause) pair in the project. Async + bounded server-side.
    checkProject: (projectId) => client.post(`${API_BASE_URL}/judge/project/${projectId}/check`),
    // Export the project's judge verdicts as an .xlsx audit snapshot (blob download).
    exportVerdicts: ({ projectId } = {}) =>
      client.get(`${API_BASE_URL}/judge/verdicts/export`, { params: { project_id: projectId }, responseType: 'blob' }),
    checkEntities: (projectId, entities) => client.post(`${API_BASE_URL}/judge/entities/check`, { project_id: projectId, entities }),

    // Called by: ComplianceStandardDetailDrawer (compare tab)
    compareStandardVersions: (v1Id, v2Id) => client.post(`${API_BASE_URL}/compliance/standards/versions/compare`, {
        version1_id: v1Id,
        version2_id: v2Id,
        entity_type: 'compliance_standard'
    }),

    // COMPLIANCE TRACEABILITY MATRIX
    getComplianceMatrix: (projectId) => client.get(`${API_BASE_URL}/compliance/matrix`, { params: { project_id: projectId } }),
    getComplianceCoverage: (projectId) => client.get(`${API_BASE_URL}/compliance/coverage`, { params: { project_id: projectId } }),
    getComplianceHistory: (projectId, drawingId) => client.get(`${API_BASE_URL}/compliance/history`, { params: { project_id: projectId, drawing_id: drawingId } }),
    getComplianceAuditLog: (projectId, limit = 50, offset = 0) => client.get(`${API_BASE_URL}/compliance/audit-log`, { params: { project_id: projectId, limit, offset } }),
    exportComplianceMatrix: (projectId) => client.get(`${API_BASE_URL}/compliance/matrix/export`, { params: { project_id: projectId }, responseType: 'blob' }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROJECT KNOWLEDGE BASE
    // ═══════════════════════════════════════════════════════════════════════════
    getProjectKB: (projectId) => client.get(`${API_BASE_URL}/projects/${projectId}/kb`),
    removeKBDocument: (projectId, documentId) => client.delete(`${API_BASE_URL}/projects/${projectId}/kb/documents/${documentId}`),
    toggleKBContext: (projectId, documentId) => client.patch(`${API_BASE_URL}/projects/${projectId}/kb/documents/${documentId}/context`),
    searchKB: (projectId, query, topK = 8) => client.post(`${API_BASE_URL}/projects/${projectId}/kb/search`, { query, top_k: topK }),

    // ═══════════════════════════════════════════════════════════════════════════
    // PROCESSING JOBS (async extraction pipeline)
    // Response: getJobStatus → { job_id, status, progress, stage, ... }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: useParallelUpload hook (poll extraction progress after upload returns 202)
    getJobStatus: (jobId) => client.get(`${API_BASE_URL}/processing/jobs/${jobId}`),
    getBatchJobStatus: (jobIds) => client.post(`${API_BASE_URL}/processing/jobs/batch-status`, { job_ids: jobIds }),
    getActiveJobs: (projectId) => client.get(`${API_BASE_URL}/processing/projects/${projectId}/active`),

    // ═══════════════════════════════════════════════════════════════════════════
    // CHAT HISTORY
    // Response: getChatHistory → { messages: [...] }
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: ChatContainer (load persisted messages on mount)
    getChatHistory: (sessionId) => client.get(`${API_BASE_URL}/chat/history/${sessionId}`),

    // ═══════════════════════════════════════════════════════════════════════════
    // PRIVACY & GDPR
    // Response: exportUserData → file download (JSON), getDataRetentionInfo → { policies: [...] }
    //           getConsentStatus → { consents: {...} }
    // Side-effect: requestAccountDeletion starts an irreversible deletion process
    //   (unless cancelled within the grace period via cancelAccountDeletion).
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: (SettingsPage privacy tab — not using api.js, uses client directly)
    // These endpoints exist for future integration or direct usage.
    exportUserData: () => client.get(`${API_BASE_URL}/privacy/export`),
    getDataRetentionInfo: () => client.get(`${API_BASE_URL}/privacy/data-retention`),
    requestAccountDeletion: (confirmation, reason, feedback) => client.delete(
        `${API_BASE_URL}/privacy/account`,
        { data: { confirmation, reason, feedback } }
    ),
    cancelAccountDeletion: () => client.post(`${API_BASE_URL}/privacy/account/cancel-deletion`),
    getConsentStatus: () => client.get(`${API_BASE_URL}/privacy/consent`),
    updateConsent: (consent) => client.put(`${API_BASE_URL}/privacy/consent`, consent),

    // ═══════════════════════════════════════════════════════════════════════════
    // SHAREPOINT / ONEDRIVE (Microsoft 365 file connector)
    // ═══════════════════════════════════════════════════════════════════════════
    // Called by: SharePointPicker (check connection status)
    getSharePointStatus: () => client.get(`${API_BASE_URL}/sharepoint/status`),
    // Called by: SharePointPicker (list available drives)
    getSharePointDrives: () => client.get(`${API_BASE_URL}/sharepoint/drives`),
    // Called by: SharePointPicker (browse folder contents)
    browseSharePoint: (driveId, folderId = 'root') => client.get(`${API_BASE_URL}/sharepoint/browse`, { params: { drive_id: driveId, folder_id: folderId } }),
    // Called by: SharePointPicker (import file to EIS project)
    importFromSharePoint: (data) => client.post(`${API_BASE_URL}/sharepoint/import`, data),
    // Called by: Settings (disconnect Microsoft 365)
    disconnectSharePoint: () => client.delete(`${API_BASE_URL}/sharepoint/disconnect`),

    // ═══════════════════════════════════════════════════════════════════════════
    // CONNECTORS / INTEGRATIONS (Odoo, Altium, SolidWorks, AutoCAD, generic ERP)
    // ═══════════════════════════════════════════════════════════════════════════
    // Per-organization (group_id) credential records. Credentials are encrypted
    // at rest and never returned by the API.
    getConnectors: () => client.get(`${API_BASE_URL}/connectors`),
    getConnector: (id) => client.get(`${API_BASE_URL}/connectors/${id}`),
    createConnector: (payload) => client.post(`${API_BASE_URL}/connectors`, payload),
    updateConnector: (id, payload) => client.patch(`${API_BASE_URL}/connectors/${id}`, payload),
    deleteConnector: (id) => client.delete(`${API_BASE_URL}/connectors/${id}`),
    testConnector: (id) => client.post(`${API_BASE_URL}/connectors/${id}/test`),

    // ═══════════════════════════════════════════════════════════════════════════
    // M18 — VALIDATION (rules + inference findings)
    // ═══════════════════════════════════════════════════════════════════════════
    // Rules: list (base library + tenant overrides), CRUD on tenant rules.
    listValidationRules: (projectId, { ruleType, enabledOnly, reviewPurpose } = {}) =>
        client.get(`${API_BASE_URL}/validation/rules`, {
            params: {
                project_id: projectId,
                rule_type: ruleType,
                enabled_only: enabledOnly,
                review_purpose: reviewPurpose,
            },
        }),
    createValidationRule: (payload) => client.post(`${API_BASE_URL}/validation/rules`, payload),
    updateValidationRule: (ruleId, patch) =>
        client.patch(`${API_BASE_URL}/validation/rules/${ruleId}`, patch),
    deleteValidationRule: (ruleId) => client.delete(`${API_BASE_URL}/validation/rules/${ruleId}`),

    // Run validation. Body: { project_id, engineering_item_id?, drawing_id?, review_purpose? }
    runValidation: (payload) => client.post(`${API_BASE_URL}/validation/run`, payload),

    // Findings: list (with filters), single get, status transition.
    listValidationFindings: (projectId, filters = {}) =>
        client.get(`${API_BASE_URL}/validation/findings`, {
            params: {
                project_id: projectId,
                severity: filters.severity,
                status: filters.status,
                target_entity_type: filters.targetEntityType,
                target_entity_id: filters.targetEntityId,
                run_id: filters.runId,
                drawing_id: filters.drawingId,
                limit: filters.limit,
                offset: filters.offset,
            },
        }),
    getValidationFinding: (findingId) =>
        client.get(`${API_BASE_URL}/validation/findings/${findingId}`),
    updateValidationFindingStatus: (findingId, status, note) =>
        client.patch(`${API_BASE_URL}/validation/findings/${findingId}`, { status, note }),

    // M18 Tier 1 — rule ingestion (T31)
    extractRuleProposals: (projectId, documentId, sourceType = 'document') =>
        client.post(`${API_BASE_URL}/validation/extract`, {
            project_id: projectId,
            document_id: documentId,
            source_type: sourceType,
        }),
    acceptRuleProposal: (projectId, extractionRunId, proposal) =>
        client.post(`${API_BASE_URL}/validation/rules/from-proposal`, {
            project_id: projectId,
            extraction_run_id: extractionRunId,
            proposal,
        }),
    listRuleSources: (ruleId) =>
        client.get(`${API_BASE_URL}/validation/rules/${ruleId}/sources`),

    // ── V&V Runner ─────────────────────────────────────────────────────────
    // Endpoints under `${API_BASE_URL}/vv`. Cross-tenant ids return 404.
    // Photo evidence reuses the existing signed-URL flow via
    // `requestSignedUploadUrl({ file_type: 'fat-evidence' })` (S3 key prefix is
    // still `fat-evidence/` — internal, not user-visible; see FAT→V&V retirement).

    listVVSessions: (params) => client.get(`${API_BASE_URL}/vv/sessions`, { params }),
    createVVSession: (data) => client.post(`${API_BASE_URL}/vv/sessions`, data),
    getVVSession: (sessionId) => client.get(`${API_BASE_URL}/vv/sessions/${sessionId}`),
    // Verification adequacy — does each step's criterion verify its requirement?
    // → { session_id, overall, counts, evaluated, findings: [{step_id, requirement_id,
    //     verdict, rule, parameter_name, requirement_interval, criterion_interval}] }.
    getVVVerificationAdequacy: (sessionId) =>
        client.get(`${API_BASE_URL}/vv/sessions/${sessionId}/verification-adequacy`),
    updateVVSession: (sessionId, patch) =>
        client.patch(`${API_BASE_URL}/vv/sessions/${sessionId}`, patch),
    deleteVVSession: (sessionId) => client.delete(`${API_BASE_URL}/vv/sessions/${sessionId}`),
    startVVSession: (sessionId) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/start`),
    pauseVVSession: (sessionId, reason) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/pause`, reason ? { reason } : {}),
    resumeVVSession: (sessionId) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/resume`),
    abortVVSession: (sessionId, reason) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/abort`, { reason }),
    signOffVVSession: (sessionId, { signature_name, notes, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/sign-off`, {
            signature_name,
            notes,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),

    listVVSteps: (sessionId) =>
        client.get(`${API_BASE_URL}/vv/sessions/${sessionId}/steps`),
    createVVSteps: (sessionId, steps) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/steps`, { steps }),
    updateVVStep: (stepId, patch) =>
        client.patch(`${API_BASE_URL}/vv/steps/${stepId}`, patch),
    passVVStep: (stepId, body = {}) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/pass`, body),
    failVVStep: (stepId, body) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/fail`, body),
    blockVVStep: (stepId, body) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/block`, body),
    retryVVStep: (stepId, body = {}) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/retry`, body),

    createVVEvidence: (stepId, data) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/evidence`, data),
    listVVStepEvidence: (stepId) =>
        client.get(`${API_BASE_URL}/vv/steps/${stepId}/evidence`),
    listVVSessionEvidence: (sessionId) =>
        client.get(`${API_BASE_URL}/vv/sessions/${sessionId}/evidence`),

    // Phase 3C — trace-graph linkage. `links` is a partial { linked_requirement_id,
    // linked_drawing_id, linked_engineering_item_id, linked_standard_clause_id }.
    // Pass null for a key to clear that link. Reuses PATCH /steps/{id}.
    patchVVStepLinks: (stepId, links) =>
        client.patch(`${API_BASE_URL}/vv/steps/${stepId}`, links),
    getVVStepContext: (stepId) =>
        client.get(`${API_BASE_URL}/vv/steps/${stepId}/context`),

    // Phase 3B.1 — AI photo measurement extraction. Synchronous (~3-8s typical).
    // body = { evidence_id } (preferred) or { photo_s3_key } (escape hatch).
    extractVVMeasurement: (stepId, body) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/extract-measurement`, body),
    // Operator correction of the AI reading. `reason` is required server-side.
    overrideVVMeasurement: (stepId, body) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/operator-override`, body),

    // Phase 3B.3 — measurement cost telemetry. Trailing window in days (1-365).
    getVVMeasurementCost: (projectId, { days = 30 } = {}) =>
        client.get(`${API_BASE_URL}/vv/projects/${projectId}/measurement-cost`, {
            params: { days },
        }),

    // Phase 4 — FAT plan auto-generator. Seeds FATStep drafts on a DRAFT session from
    // a set of requirement_ids. Returns { generated, skipped, tolerance_sources }.
    // Pass dry_run=true to preview without persisting. Non-draft sessions return 409.
    autoGenerateVVSteps: (sessionId, { requirementIds, verificationMethodFilter = null, dryRun = false }) =>
        client.post(`${API_BASE_URL}/vv/sessions/${sessionId}/auto-generate-steps`, {
            requirement_ids: requirementIds,
            verification_method_filter: verificationMethodFilter,
            dry_run: dryRun,
        }),

    // Phase 6 — RCA. Diagnose a failed FAT step. Returns { cache_id, cache_hit,
    // candidates: [{entity_type, entity_id, score, reasoning, evidence_links}],
    // top_decision_entry_id, model_used, cost_usd }. Non-failed step → 409;
    // cached unless forceRefresh=true (cache invalidates automatically when the
    // step's observation / AI reading / linked entities / DUT change).
    requestRCA: (stepId, { forceRefresh = false } = {}) =>
        client.post(`${API_BASE_URL}/vv/steps/${stepId}/rca`, {
            force_refresh: forceRefresh,
        }),

    // FAT session report export (Alt-A slice). Compiles a structured JSON
    // snapshot of a signed-off session — header + steps + evidence (with
    // short-lived signed S3 URLs) + rca_confirmed entries + report_hash
    // anchored to session.signature_hash. Non-completed session → 400.
    getVVReport: (sessionId) =>
        client.get(`${API_BASE_URL}/vv/sessions/${sessionId}/report`),

    // ── Quality Events (NCR → CAPA) ──────────────────────────────────────────
    // Closes the loop FAT fail → RCA → NCR → CAPA → verified close. NCRs raise
    // from a confirmed RCA cause (source_fat_step_id + source_decision_entry_id)
    // or manually. See constants/qualityEvent.js for the two state machines.
    createNCR: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ncrs`, data),
    listNCRs: (projectId, { status = null, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ncrs`, {
            params: { status, limit, offset },
        }),
    getNCR: (projectId, ncrId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/ncrs/${ncrId}`),
    // action ∈ { investigate, close_no_action }. close_no_action requires disposition.
    transitionNCR: (projectId, ncrId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ncrs/${ncrId}/transition`, body),
    // Update investigation fields while NCR is in investigating state.
    updateNCRInvestigation: (projectId, ncrId, body) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/ncrs/${ncrId}/investigation`, body),
    // Escalate an NCR into a CAPA (idempotent). NCR moves to capa_required.
    spawnCAPA: (projectId, ncrId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/ncrs/${ncrId}/capa`),
    getCAPA: (projectId, capaId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/capas/${capaId}`),
    // action ∈ { set_root_cause, set_action_plan, close_effective, close_ineffective, replan }.
    advanceCAPA: (projectId, capaId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/capas/${capaId}/advance`, body),
    listCAPAs: (projectId, { status = null, limit = 100 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/capas`, { params: { status, limit } }),
    deleteNCR: (projectId, ncrId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/ncrs/${ncrId}`),
    deleteCAPA: (projectId, capaId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/capas/${capaId}`),
    // Summary counts by status for NCR / CAPA / PA.
    getQualitySummary: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/quality-events/summary`),
    // Preventive Actions (ISO 13485 §8.5.3) — proactive, not from NCR.
    createPA: (projectId, data) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/preventive-actions`, data),
    listPAs: (projectId, { status = null, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/preventive-actions`, {
            params: { status, limit, offset },
        }),
    getPA: (projectId, paId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/preventive-actions/${paId}`),
    // action ∈ { plan, close }
    advancePA: (projectId, paId, body) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/preventive-actions/${paId}/advance`, body),
    deletePA: (projectId, paId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/preventive-actions/${paId}`),

    // ── V&V spine ────────────────────────────────────────────────────────────
    // One snapshot of the whole verification axis: requirements coverage →
    // verification verdicts → validation (FAT) → quality events → conformity.
    // Each block is best-effort ({...} or {error}); render blocks independently.
    getVVSummary: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/vv-summary`),

    // ── Safety / Compliance ontology (Q1 sprint week 1-3, READ-ONLY v1) ──
    // ISO 12100 / 13849 / 61508 alignment. Write path lands with the dedicated
    // Safety section UI in a follow-up slice.
    listSafetyFunctions: (projectId, { includeDeleted = false, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/safety-functions`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getSafetyFunction: (projectId, safetyFunctionId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/safety-functions/${safetyFunctionId}`),
    createSafetyFunction: (projectId, payload) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/safety/safety-functions`, payload),
    updateSafetyFunction: (projectId, safetyFunctionId, payload) =>
        client.patch(
            `${API_BASE_URL}/projects/${projectId}/safety/safety-functions/${safetyFunctionId}`,
            payload,
        ),
    deleteSafetyFunction: (projectId, safetyFunctionId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/safety/safety-functions/${safetyFunctionId}`),
    // ISO 13849-1 PL calculator — Sistema-parity at SMB level. `persist=true`
    // overwrites achieved_PL on the row + fires a DecisionJournal entry when
    // achieved < PLr; persist=false is a what-if dry-run.
    calculateSafetyFunctionPL: (projectId, safetyFunctionId, payload) =>
        client.post(
            `${API_BASE_URL}/projects/${projectId}/safety/safety-functions/${safetyFunctionId}/pl-calculation`,
            payload,
        ),

    listHazards: (projectId, { includeDeleted = false, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/hazards`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getHazard: (projectId, hazardId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/hazards/${hazardId}`),

    listHazardousSituations: (projectId, { includeDeleted = false, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/hazardous-situations`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getHazardousSituation: (projectId, hazardousSituationId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/hazardous-situations/${hazardousSituationId}`),

    listRiskReductionMeasures: (projectId, { includeDeleted = false, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/risk-reduction-measures`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getRiskReductionMeasure: (projectId, measureId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/risk-reduction-measures/${measureId}`),

    listResidualRisks: (projectId, { includeDeleted = false, limit = 50, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/residual-risks`, {
            params: { include_deleted: includeDeleted, limit, offset },
        }),
    getResidualRisk: (projectId, residualRiskId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/safety/residual-risks/${residualRiskId}`),

    // Hazard write path (ISO 12100 §3.6)
    createHazard: (projectId, payload) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/safety/hazards`, payload),
    updateHazard: (projectId, hazardId, payload) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/safety/hazards/${hazardId}`, payload),
    deleteHazard: (projectId, hazardId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/safety/hazards/${hazardId}`),

    // HazardousSituation write path (ISO 12100 §3.10)
    createHazardousSituation: (projectId, payload) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/safety/hazardous-situations`, payload),
    updateHazardousSituation: (projectId, situationId, payload) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/safety/hazardous-situations/${situationId}`, payload),
    deleteHazardousSituation: (projectId, situationId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/safety/hazardous-situations/${situationId}`),

    // RiskReductionMeasure write path (ISO 12100 §6)
    createRiskReductionMeasure: (projectId, payload) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/safety/risk-reduction-measures`, payload),
    updateRiskReductionMeasure: (projectId, measureId, payload) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/safety/risk-reduction-measures/${measureId}`, payload),
    deleteRiskReductionMeasure: (projectId, measureId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/safety/risk-reduction-measures/${measureId}`),

    // ResidualRisk write path (ISO 12100 §3.13)
    createResidualRisk: (projectId, payload) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/safety/residual-risks`, payload),
    updateResidualRisk: (projectId, residualRiskId, payload) =>
        client.patch(`${API_BASE_URL}/projects/${projectId}/safety/residual-risks/${residualRiskId}`, payload),
    deleteResidualRisk: (projectId, residualRiskId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/safety/residual-risks/${residualRiskId}`),

    // MR 2027 Declaration of Conformity. Preview-only draft + sign + list + get.
    // Draft never writes; sign supersedes the prior signed DoC and returns the
    // immutable snapshot with content_hash + signature_hash anchored to FAT.
    generateDoCDraft: (projectId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/doc/draft`),
    signDoC: (projectId, { signatoryName, signatoryRole, applicableRegulations, notifiedBodyInfo = null, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/doc/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            applicable_regulations: applicableRegulations,
            notified_body_info: notifiedBodyInfo,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),
    listDoCs: (projectId, { includeSuperseded = false } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/doc`, {
            params: { include_superseded: includeSuperseded },
        }),
    getDoC: (projectId, docId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/doc/${docId}`),

    // EU MDR Annex II Technical File. Whole-device compile is async: buildTechnicalFile
    // returns { job_id }, poll getJobStatus(jobId) → result_json.tf_id, then getTechnicalFile.
    // sign freezes a reviewed draft (supersedes the prior signed one). export is a blob.
    buildTechnicalFile: (projectId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/technical-file/build`),
    listTechnicalFiles: (projectId, { includeSuperseded = false, includeDrafts = false } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/technical-file`, {
            params: { include_superseded: includeSuperseded, include_drafts: includeDrafts },
        }),
    getTechnicalFile: (projectId, tfId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/technical-file/${tfId}`),
    signTechnicalFile: (projectId, tfId, { signatoryName, signatoryRole, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/technical-file/${tfId}/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),
    exportTechnicalFile: (projectId, tfId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/technical-file/${tfId}/export.docx`, {
            responseType: 'blob',
        }),

    // FDA 21 CFR 820.30 / ISO 13485 §7.3 Design History File. Whole-device compile
    // is async: buildDHF returns { job_id }, poll getJobStatus(jobId) → result_json.dhf_id,
    // then getDHF. sign freezes a reviewed draft (supersedes prior). export = blob.
    buildDHF: (projectId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/dhf/build`),
    listDHFs: (projectId, { includeSuperseded = false, includeDrafts = false } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/dhf`, {
            params: { include_superseded: includeSuperseded, include_drafts: includeDrafts },
        }),
    getDHF: (projectId, dhfId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/dhf/${dhfId}`),
    signDHF: (projectId, dhfId, { signatoryName, signatoryRole, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/dhf/${dhfId}/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),
    exportDHF: (projectId, dhfId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/dhf/${dhfId}/export.docx`, {
            responseType: 'blob',
        }),

    // Part 11 §11.10(e) audit trail — append-only, hash-chained change log.
    getAuditEvents: (projectId, { entityType, entityId, limit = 100, offset = 0 } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/audit-events`, {
            params: { entity_type: entityType, entity_id: entityId, limit, offset },
        }),
    verifyAuditChain: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/audit-events/verify`),
    exportAuditTrail: (projectId, { entityType, entityId } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/audit-events/export.csv`, {
            params: { entity_type: entityType, entity_id: entityId },
            responseType: 'blob',
        }),

    // Part 11 electronic signature on living entities (Phase 3). Author ≠ signer.
    signDocument: (documentId, { signatoryName, signatoryRole, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/documents/${documentId}/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),
    signRequirement: (requirementId, { signatoryName, signatoryRole, signatureMeaning = 'approved', reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/requirements/${requirementId}/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),

    // Sequential multi-signature (Tier 2). entityType: 'document' | 'requirement'.
    getSignatureSequence: (entityType, entityId) =>
        client.get(`${API_BASE_URL}/${entityType}/${entityId}/signature-sequence`),
    defineSignatureSequence: (entityType, entityId, { steps, allowRepeatSigners = false }) =>
        client.post(`${API_BASE_URL}/${entityType}/${entityId}/signature-sequence`, {
            steps,
            allow_repeat_signers: allowRepeatSigners,
        }),
    signSignatureStep: (entityType, entityId, { signatoryName, signatoryRole, signatureMeaning, reauthCredential, mfaCode = null }) =>
        client.post(`${API_BASE_URL}/${entityType}/${entityId}/signature-sequence/sign`, {
            signatory_name: signatoryName,
            signatory_role: signatoryRole,
            signature_meaning: signatureMeaning,
            reauth_credential: reauthCredential,
            mfa_code: mfaCode,
        }),
    rejectSignatureSequence: (entityType, entityId, { reason }) =>
        client.post(`${API_BASE_URL}/${entityType}/${entityId}/signature-sequence/reject`, { reason }),

    // First-class design reviews (FDA 21 CFR 820.30(e) / ISO 13485 §7.3.5).
    // list → { items: [{ review_id, stage, decision, reviewers, action_items,
    // reviewed_refs, independent_attested, independence_status, review_date }], total }.
    listDesignReviews: (projectId) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/design-reviews`),
    createDesignReview: (projectId, { stage, decision, reviewers = [], actionItems = [], reviewedRefs = [], independentAttested = false }) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/design-reviews`, {
            stage,
            decision,
            reviewers,
            action_items: actionItems,
            reviewed_refs: reviewedRefs,
            independent_attested: independentAttested,
        }),

    // 21 CFR Part 11 §11.10(g) authorized-signer allowlist (Phase 3b). Opt-in:
    // empty = open. Management (add/remove) is project-owner-gated (403 otherwise).
    listProjectSigners: (projectId, { includeRevoked = false } = {}) =>
        client.get(`${API_BASE_URL}/projects/${projectId}/signers`, { params: { include_revoked: includeRevoked } }),
    addProjectSigner: (projectId, userId, { identityVerificationMethod = null, qualification = null } = {}) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/signers`, {
            user_id: userId,
            identity_verification_method: identityVerificationMethod,
            qualification,
        }),
    removeProjectSigner: (projectId, userId) =>
        client.delete(`${API_BASE_URL}/projects/${projectId}/signers/${userId}`),
    attestProjectSigner: (projectId, userId) =>
        client.post(`${API_BASE_URL}/projects/${projectId}/signers/${userId}/attest`),

    // AI Act Article 12 — decision-log chain inspection (Phase 0 Sprint 3a).
    // `getAIActDecisionTrace` walks the chain root → leaf for a given
    // decision_id and surfaces the integrity check result. `getAIActLoggingSummary`
    // returns the project-scoped aggregate posture used by the admin trace
    // view header card + the DoC §AI Act §12 footer renderer.
    getAIActDecisionTrace: (decisionId) =>
        client.get(`${API_BASE_URL}/ai-act/decision-trace/${decisionId}`),
    getAIActLoggingSummary: (projectId) =>
        client.get(`${API_BASE_URL}/ai-act/projects/${projectId}/logging-summary`),

    // Phase 1 Sprint C — one-stop notified-body audit packet, pivoted on
    // a StandardClause. Strict-Pydantic locked on the BE; the FE renders
    // each section as a printable subsection (FATReportView pattern).
    buildAuditPackage: (
        projectId,
        clauseId,
        { includeSuperseded = false, decisionsLookbackDays = 365 } = {},
    ) =>
        client.post(`${API_BASE_URL}/audit-package/build`, {
            project_id: projectId,
            clause_id: clauseId,
            include_superseded: includeSuperseded,
            decisions_lookback_days: decisionsLookbackDays,
        }),

    // Traceability coverage + clause search
    getTraceabilityCoverage: (projectId, { standardId, gapOnly } = {}) =>
        client.get(`/api/v1/projects/${projectId}/traceability/coverage`, {
            params: { standard_id: standardId, gap_only: gapOnly },
        }),
    searchStandardClauses: (projectId, q, { limit = 10, standardId } = {}) =>
        client.get(`/api/v1/projects/${projectId}/traceability/clauses/search`, {
            params: { q, limit, standard_id: standardId },
        }),

    // Signed-URL upload helper — used by FATPhotoUploader.
    // Returns { url, method, headers, s3_key, file_id, ... }. Caller PUTs the
    // file bytes to `url` then POSTs the resulting s3_key to whichever
    // evidence-create endpoint they need.
    requestSignedUploadUrl: ({ filename, content_type, project_id, file_type, expiration }) =>
        client.post(`${API_BASE_URL}/signed-urls/upload`, {
            filename,
            content_type: content_type || 'application/octet-stream',
            project_id,
            file_type: file_type || 'documentation',
            expiration: expiration || 900,
        }),

};
